import { Button, Card, Code, List, ListItem, Stack, Text } from '@mantine/core'
import { IconDownload, IconRefresh } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { CopyableText } from '@/components/ui-shared/CopyableText/CopyableText'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { PLATFORM_NAME } from '@/config/main'

const VP_INSTALLER_URL = 'http://bit.ly/vp-installer'

const JETSON_MANUAL =
  'https://drive.google.com/file/d/1SKPIXZnP2Rj6jU9ezhf5FkmjqHdKrr4a/view?usp=sharing'

type Translation = {
  id: string
  values?: Record<string, string | number>
}

type Instruction = Translation & {
  codeExample?: string
}

type Step = {
  title: Translation
  instructions?: Instruction[]
}

type StepsStructure = Record<number, Step>

const STEPS: StepsStructure = {
  1: {
    title: {
      id: 'devices.register.steps.1.title'
    }
  },
  2: {
    title: {
      id: 'devices.register.steps.2.title',
      values: { appName: PLATFORM_NAME }
    },
    instructions: [
      {
        id: 'devices.register.steps.2.instruction.1'
      },
      {
        id: 'devices.register.steps.2.instruction.2'
      },
      {
        id: 'devices.register.steps.2.instruction.3'
      },
      {
        id: 'devices.register.steps.2.instruction.4',
        codeExample: `sudo bash -c "$(wget -qO - ${VP_INSTALLER_URL})"`
      },
      {
        id: 'devices.register.steps.2.instruction.5',
        values: { appName: PLATFORM_NAME }
      }
    ]
  },
  3: {
    title: {
      id: 'devices.register.steps.3.title'
    }
  }
}

type DeviceRegistrationStepsProps = {
  onRefresh: () => void
}

export const DeviceRegistrationSteps = ({
  onRefresh
}: DeviceRegistrationStepsProps) => {
  return (
    <Stack>
      <Text size="sm">
        <FormattedMessage
          id="devices.register.steps.intro"
          values={{ appName: PLATFORM_NAME }}
        />
      </Text>

      <Card bg="gray.1">
        <List size="xs" spacing="md">
          {Object.entries(STEPS).map(([stepNumber, step]) => (
            <ListItem key={stepNumber}>
              <Text size="xs" fw={600}>
                <FormattedMessage
                  id={step.title.id}
                  values={step.title.values}
                />
              </Text>

              {step.instructions && (
                <List size="xs" spacing="xs" mt="xs" withPadding>
                  {step.instructions.map((instruction) => (
                    <ListItem key={instruction.id}>
                      <FormattedMessage
                        id={instruction.id}
                        values={instruction.values}
                      />

                      {instruction.codeExample && (
                        <CopyableText text={instruction.codeExample}>
                          <Code color="black" c="white" block>
                            {instruction.codeExample}
                          </Code>
                        </CopyableText>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </ListItem>
          ))}
        </List>
      </Card>

      <PlatformAlert compact>
        <FormattedMessage id="devices.register.steps.help" />
      </PlatformAlert>

      <Button
        rightSection={<IconDownload size={16} />}
        variant="outline"
        component="a"
        href={JETSON_MANUAL}
        target="_blank"
        fullWidth
      >
        <FormattedMessage id="devices.register.buttons.downloadManual" />
      </Button>

      <Button
        rightSection={<IconRefresh size={16} />}
        fullWidth
        onClick={onRefresh}
      >
        <FormattedMessage id="devices.register.buttons.refresh" />
      </Button>
    </Stack>
  )
}
